<template>
  <div class="login-body-polished">
    <div class="container">
      <!-- Left Panel - Branding & Info -->
      <div class="left-panel">
        <div class="brand-content">
          <div class="logo-container">
            <div class="logo">
              <img src="@/assets/logo.png" alt="BidSnap AI Logo" width="56" height="56"
                class="logo-style">
            </div>
            <h1 class="brand-name">BidSnap AI</h1>
          </div>

          <div class="hero-content text-left">
            <h2 class="hero-title text-left">Reset your password securely</h2>
            <p class="hero-description text-left">Enter your email address and we'll send you instructions to reset your password and regain access to your account.</p>

            <div class="features">
              <!-- Feature 1 -->
              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="block mb-2px" style="font-weight: 600;">Secure process</div>
                  <div class="text-sm opacity-90 leading-tight">Your password reset link is encrypted and expires after 24 hours for security.</div>
                </div>
              </div>

              <!-- Feature 2 -->
              <div class="feature-item">
                <div class="feature-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="flex-1">
                  <div class="block mb-2px" style="font-weight: 600;">Quick recovery</div>
                  <div class="text-sm opacity-90 leading-tight">Receive your reset link instantly and regain access to your account in minutes.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="decorative-elements">
          <div class="blob blob-1"></div>
          <div class="blob blob-2"></div>
          <div class="blob blob-3"></div>
        </div>
      </div>

      <!-- Right Panel - Reset Form -->
      <div class="right-panel">
        <div class="login-container">
          <div class="login-header text-left">
            <h2 class="login-title text-left">Forgot your password?</h2>
            <p class="login-subtitle text-left">No worries! Enter your email and we'll send you reset instructions.</p>
          </div>

          <form class="login-form" @submit.prevent="handlePasswordReset">
            <!-- Success/Error Message -->
            <div v-if="successMessage" style="background: rgba(76, 175, 80, 0.1); color: #4CAF50; padding: 0.875rem; border-radius: 0.5rem; margin-bottom: 1rem; font-size: 0.9rem; text-align: center;">
              {{ successMessage }}
            </div>
            <!-- Email Input -->
            <div class="form-group">
              <label for="email" class="form-label block text-left">Email address</label>
              <div class="input-wrapper">
                <svg class="input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M22 6L12 13L2 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <input type="email" id="email" class="form-input" :class="{ 'error-input': errors.email }" placeholder="you@example.com" v-model="email">
              </div>
              <p v-if="errors.email" class="error-msg">{{ errorMessage }}</p>
            </div>

            <button type="submit" class="btn-primary">
              <span class="btn-text">Send Reset Link</span>
              <svg class="btn-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12 5L19 12L12 19" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </form>

          <div class="text-center mt-6 text-gray-600">
            Remember your password? <a @click.prevent="routeLogin" href="#" class="signup-link-style">Sign In</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForgotPassword',
  


  data() {
    return {
      email: '',
      successMessage: '',
      errorMessage: '',
      errors: {}
    }
  },



  methods: {
    routeLogin() {
      this.$router.push({ name: 'LoginPage' });
    },

    handlePasswordReset() {
      this.successMessage = '';
      this.errorMessage = '';
      this.errors = {};

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!this.email.trim()) {
        this.errors.email = true;
        this.errorMessage = 'Please enter your email address.';
        return;
      }
      if (!emailRegex.test(this.email)) {
        this.errors.email = true;
        this.errorMessage = 'Please enter a valid email address.';
        return;
      }

      // TODO: Implement actual password reset API call
      // For now, show success message
      this.successMessage = 'Password reset link has been sent to your email!';
      
      // Optionally redirect to login after a delay
      setTimeout(() => {
        this.routeLogin();
      }, 3000);
    }
  }
}
</script>

<style scoped>
/* With the header visible, fill the space under it instead of forcing
   100vh (same fix as the login page). */
.login-body-polished {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.login-body-polished .container {
  height: 100%;
  flex: 1;
  min-height: 0;
}
</style>